import { useEffect, useMemo, useRef, useState } from 'react';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import api from '../lib/api';
import { motion, AnimatePresence } from 'framer-motion';
import { MapPin, Users2, Radio, Loader2, Filter, Flame, Layers, Compass, Sparkles } from 'lucide-react';
import { toast } from 'sonner';

mapboxgl.accessToken = process.env.REACT_APP_MAPBOX_TOKEN || '';

const MAP_STYLE = 'mapbox://styles/mapbox/satellite-streets-v12';

const CATEGORY_COLORS = {
  Birds: '#E9C46A',
  Mammals: '#F4A261',
  Plants: '#95D5B2',
  Trees: '#40916C',
  Reptiles: '#B7E4C7',
  Amphibians: '#74C69D',
  Fish: '#8ECAE6',
  Insects: '#D4A5A5',
  Mushrooms: '#E76F51',
  default: '#E9C46A',
};

function markerRadius(count) {
  const r = 8 + Math.min(16, Math.log2(Math.max(count, 1)) * 5);
  return Math.round(r);
}

export default function MapPage() {
  const mapContainerRef = useRef(null);
  const mapRef = useRef(null);
  const markersRef = useRef([]);
  const hotspotMarkersRef = useRef([]);

  const [clusters, setClusters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mapReady, setMapReady] = useState(false);
  const [category, setCategory] = useState('all');
  const [rarity, setRarity] = useState('all');
  const [activeCluster, setActiveCluster] = useState(null);
  const [pulseKeys, setPulseKeys] = useState(new Set());
  const prevKeys = useRef(new Set());
  const [heatPoints, setHeatPoints] = useState([]);
  const [heatOn, setHeatOn] = useState(true);
  const [hotspotsOn, setHotspotsOn] = useState(true);
  const [hotspots, setHotspots] = useState([]);
  const errorShownRef = useRef(false);
  const didFitRef = useRef(false);

  const load = async () => {
    try {
      const r = await api.get('/map/clusters?precision=1&limit=500');
      errorShownRef.current = false;
      const nextKeys = new Set(r.data.map(c => c.cluster_key));
      const fresh = new Set();
      r.data.forEach(c => { if (!prevKeys.current.has(c.cluster_key)) fresh.add(c.cluster_key); });
      prevKeys.current = nextKeys;
      if (fresh.size) {
        setPulseKeys(fresh);
        setTimeout(() => setPulseKeys(new Set()), 3500);
      }
      setClusters(r.data);
    } catch {
      if (!errorShownRef.current) {
        errorShownRef.current = true;
        toast.error('Map failed to load', {
          description: 'Live discoveries are temporarily unavailable. Retrying…',
          duration: 4000,
        });
      }
    } finally { setLoading(false); }
  };

  const loadHeat = async () => {
    try {
      const params = new URLSearchParams({ limit: '1000' });
      if (category !== 'all') params.append('category', category);
      if (rarity !== 'all') params.append('rarity', rarity);
      const r = await api.get(`/map/heat?${params.toString()}`);
      setHeatPoints(r.data.points || []);
    } catch { setHeatPoints([]); }
  };

  const loadHotspots = async () => {
    try {
      const r = await api.get('/map/hotspots?min_finders=2&limit=25');
      setHotspots(r.data || []);
    } catch { setHotspots([]); }
  };

  // ---- Initialize Mapbox ----
  useEffect(() => {
    if (!mapContainerRef.current || mapRef.current) return;
    if (!mapboxgl.accessToken) {
      toast.error('Mapbox token missing. Contact support.');
      setLoading(false);
      return;
    }
    const map = new mapboxgl.Map({
      container: mapContainerRef.current,
      style: MAP_STYLE,
      center: [0, 20],
      zoom: 1.6,
      projection: 'globe',
      attributionControl: false,
    });
    map.addControl(new mapboxgl.NavigationControl({ visualizePitch: true }), 'top-right');
    map.addControl(new mapboxgl.AttributionControl({ compact: true }), 'bottom-right');

    map.on('style.load', () => {
      map.setFog({
        color: 'rgb(20, 40, 30)',
        'high-color': 'rgb(10, 20, 15)',
        'horizon-blend': 0.12,
        'space-color': 'rgb(4, 10, 8)',
        'star-intensity': 0.5,
      });
      // Heat source (empty by default)
      map.addSource('heat-src', {
        type: 'geojson',
        data: { type: 'FeatureCollection', features: [] },
      });
      map.addLayer({
        id: 'heat-layer',
        type: 'heatmap',
        source: 'heat-src',
        maxzoom: 12,
        paint: {
          'heatmap-weight': ['interpolate', ['linear'], ['get', 'w'], 0, 0, 1, 1],
          'heatmap-intensity': ['interpolate', ['linear'], ['zoom'], 0, 0.8, 12, 3],
          'heatmap-color': [
            'interpolate', ['linear'], ['heatmap-density'],
            0, 'rgba(58, 12, 163, 0)',
            0.2, 'rgba(58, 12, 163, 0.4)',
            0.4, 'rgba(114, 9, 183, 0.55)',
            0.6, 'rgba(247, 37, 133, 0.7)',
            0.8, 'rgba(244, 162, 97, 0.85)',
            1, 'rgba(233, 196, 106, 1)',
          ],
          'heatmap-radius': ['interpolate', ['linear'], ['zoom'], 0, 8, 12, 40],
          'heatmap-opacity': ['interpolate', ['linear'], ['zoom'], 8, 0.9, 12, 0.4],
        },
      });
      setMapReady(true);
    });
    mapRef.current = map;
    return () => { map.remove(); mapRef.current = null; };
  }, []);

  // ---- Initial + polling data load ----
  useEffect(() => {
    load(); loadHeat(); loadHotspots();
    const iv = setInterval(load, 20000);
    return () => clearInterval(iv);
  }, []);

  useEffect(() => { loadHeat(); }, [category, rarity]);

  // ---- Update heat layer when heatPoints/heatOn change ----
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapReady) return;
    const src = map.getSource('heat-src');
    if (!src) return;
    const features = heatOn
      ? heatPoints.map(p => ({
          type: 'Feature',
          properties: { w: (p[2] ?? 0.5) },
          geometry: { type: 'Point', coordinates: [p[1], p[0]] },
        }))
      : [];
    src.setData({ type: 'FeatureCollection', features });
  }, [heatPoints, heatOn, mapReady]);

  // ---- Filtered visible clusters ----
  const visible = clusters.filter(c =>
    (category === 'all' || c.species_category === category) &&
    (rarity === 'all' || (c.species_rarity || 'common') === rarity)
  );

  // ---- Auto-fit once when first clusters arrive ----
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapReady || didFitRef.current || visible.length === 0) return;
    const b = new mapboxgl.LngLatBounds();
    visible.forEach(c => b.extend([c.longitude, c.latitude]));
    if (!b.isEmpty()) {
      map.fitBounds(b, { padding: 60, maxZoom: 6, duration: 1600 });
      didFitRef.current = true;
    }
  }, [visible, mapReady]);

  // ---- Render discovery cluster markers (animated) ----
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapReady) return;
    markersRef.current.forEach(m => m.remove());
    markersRef.current = [];
    visible.forEach(c => {
      const color = CATEGORY_COLORS[c.species_category] || CATEGORY_COLORS.default;
      const isFresh = pulseKeys.has(c.cluster_key);
      const size = markerRadius(c.unique_finders || c.count) * 1.6;
      const el = document.createElement('div');
      el.className = 'wq-marker';
      el.setAttribute('data-testid', 'map-cluster-marker');
      el.style.cssText = `
        width:${size}px;height:${size}px;border-radius:9999px;
        background:${color};box-shadow:0 0 24px -2px ${color}, inset 0 0 8px rgba(255,255,255,0.35);
        border:${isFresh ? '2px solid #5EEAD4' : `2px solid ${color}`};
        opacity:${isFresh ? 1 : 0.85};cursor:pointer;
        display:flex;align-items:center;justify-content:center;
        transition:transform 180ms ease;
      `;
      if (isFresh) {
        const pulse = document.createElement('div');
        pulse.style.cssText = `
          position:absolute;inset:-8px;border-radius:9999px;
          border:2px solid #5EEAD4;animation:wq-pulse 1.8s ease-out infinite;pointer-events:none;
        `;
        el.appendChild(pulse);
      }
      el.addEventListener('mouseenter', () => { el.style.transform = 'scale(1.15)'; });
      el.addEventListener('mouseleave', () => { el.style.transform = 'scale(1)'; });
      el.addEventListener('click', (e) => { e.stopPropagation(); setActiveCluster(c); });
      const m = new mapboxgl.Marker({ element: el })
        .setLngLat([c.longitude, c.latitude])
        .addTo(map);
      markersRef.current.push(m);
    });
  }, [visible, pulseKeys, mapReady]);

  // ---- Render hotspot pulses (community meccas) ----
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapReady) return;
    hotspotMarkersRef.current.forEach(m => m.remove());
    hotspotMarkersRef.current = [];
    if (!hotspotsOn) return;
    hotspots.forEach(h => {
      if (h.latitude == null || h.longitude == null) return;
      const size = Math.min(72, 24 + h.unique_finders * 5);
      const el = document.createElement('div');
      el.setAttribute('data-testid', 'map-hotspot-marker');
      el.style.cssText = `position:relative;width:${size}px;height:${size}px;pointer-events:auto;cursor:pointer;`;
      el.innerHTML = `
        <div style="position:absolute;inset:0;border-radius:9999px;border:2px solid rgba(247,37,133,0.8);
          background:radial-gradient(circle,rgba(247,37,133,0.35),transparent 70%);
          animation:wq-hotspot 2.2s ease-out infinite;"></div>
        <div style="position:absolute;inset:35%;border-radius:9999px;background:#f72585;box-shadow:0 0 12px #f72585;"></div>
      `;
      const pop = new mapboxgl.Popup({ offset: size / 2, closeButton: false, className: 'wq-popup' })
        .setHTML(
          `<div style="color:#0b1310;min-width:200px;">
            <div style="font-family:'Fraunces',serif;font-size:16px;line-height:1.15;">
              <span style="color:#f72585">◈</span> ${h.area}
            </div>
            <div style="font-size:11px;margin-top:4px;">${h.unique_finders} explorers · ${h.unique_species} species</div>
            <div style="font-size:10px;color:#555;margin-top:2px;">${h.observations} sightings · top ${h.top_score}</div>
          </div>`
        );
      const m = new mapboxgl.Marker({ element: el })
        .setLngLat([h.longitude, h.latitude])
        .setPopup(pop)
        .addTo(map);
      hotspotMarkersRef.current.push(m);
    });
  }, [hotspots, hotspotsOn, mapReady]);

  const categories = useMemo(() => {
    const s = new Set(clusters.map(c => c.species_category).filter(Boolean));
    return ['all', ...Array.from(s).sort()];
  }, [clusters]);

  const stats = useMemo(() => ({
    pins: visible.length,
    observations: visible.reduce((a, c) => a + (c.count || 0), 0),
    finders: new Set(visible.flatMap(c => c.finders.map(f => f.user_id))).size,
  }), [visible]);

  return (
    <div className="space-y-3" data-testid="map-root">
      {/* Header stats + live badge */}
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div>
          <p className="text-[10px] uppercase tracking-[0.25em] text-accent">Community Map</p>
          <h2 className="font-serif text-2xl leading-tight">Live discoveries</h2>
        </div>
        <div className="flex items-center gap-2 px-3 py-1.5 rounded-full glass border border-white/10" data-testid="map-live-indicator">
          <span className="relative flex h-2 w-2">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-60" />
            <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500" />
          </span>
          <span className="text-[10px] uppercase tracking-widest text-muted-foreground">Live</span>
        </div>
      </div>

      {/* Stats strip */}
      <div className="grid grid-cols-3 gap-2">
        <StatChip icon={MapPin} label="Pins" value={stats.pins} />
        <StatChip icon={Radio} label="Sightings" value={stats.observations} />
        <StatChip icon={Users2} label="Finders" value={stats.finders} />
      </div>

      {/* Category filter */}
      <div className="flex items-center gap-2 overflow-x-auto pb-1 -mx-1 px-1" data-testid="map-filter-row">
        <Filter className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" />
        {categories.map(cat => (
          <button
            key={cat}
            onClick={() => setCategory(cat)}
            data-testid={`filter-${cat}`}
            className={`flex-shrink-0 text-[11px] uppercase tracking-widest px-3 py-1.5 rounded-full border transition-colors ${
              category === cat
                ? 'border-accent/60 bg-accent/10 text-accent'
                : 'border-white/10 bg-card/50 text-muted-foreground hover:text-foreground'
            }`}
          >{cat}</button>
        ))}
      </div>

      {/* Rarity + layer toggles */}
      <div className="flex items-center gap-2 overflow-x-auto pb-1 -mx-1 px-1" data-testid="map-rarity-row">
        <span className="text-[9px] uppercase tracking-widest text-muted-foreground">Rarity</span>
        {['all','common','uncommon','rare','epic','legendary','mythic'].map(r => (
          <button key={r} onClick={() => setRarity(r)} data-testid={`rarity-${r}`}
            className={`flex-shrink-0 text-[10px] uppercase tracking-widest px-2.5 py-1 rounded-full border ${
              rarity === r ? 'border-accent/60 bg-accent/10 text-accent'
                : 'border-white/10 bg-card/50 text-muted-foreground hover:text-foreground'
            }`}
          >{r}</button>
        ))}
      </div>

      <div className="flex items-center gap-2">
        <button onClick={() => setHeatOn(v => !v)} data-testid="toggle-heat"
          className={`inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest px-2.5 py-1 rounded-full border ${heatOn ? 'border-accent/60 bg-accent/10 text-accent' : 'border-white/10 bg-card/50 text-muted-foreground'}`}
        ><Flame className="w-3 h-3" /> Heat</button>
        <button onClick={() => setHotspotsOn(v => !v)} data-testid="toggle-hotspots"
          className={`inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest px-2.5 py-1 rounded-full border ${hotspotsOn ? 'border-accent/60 bg-accent/10 text-accent' : 'border-white/10 bg-card/50 text-muted-foreground'}`}
        ><Layers className="w-3 h-3" /> Hotspots</button>
        <button
          onClick={() => {
            const map = mapRef.current;
            if (!map || !visible.length) return;
            const b = new mapboxgl.LngLatBounds();
            visible.forEach(c => b.extend([c.longitude, c.latitude]));
            if (!b.isEmpty()) map.fitBounds(b, { padding: 60, maxZoom: 6, duration: 1200 });
          }}
          data-testid="map-fit-btn"
          className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest px-2.5 py-1 rounded-full border border-white/10 bg-card/50 text-muted-foreground"
        ><Compass className="w-3 h-3" /> Fit</button>
        <span className="ml-auto text-[9px] uppercase tracking-widest text-muted-foreground flex items-center gap-1">
          <Sparkles className="w-3 h-3 text-accent" /> Mapbox
        </span>
      </div>

      {/* Map */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="relative rounded-3xl overflow-hidden border border-white/10"
        style={{ height: 'calc(100vh - 340px)', minHeight: 340 }}
        data-testid="mapbox-canvas"
      >
        {loading && (
          <div className="absolute inset-0 z-[400] flex items-center justify-center bg-[#040A08]/60">
            <Loader2 className="w-5 h-5 animate-spin text-accent" />
          </div>
        )}
        <div ref={mapContainerRef} className="w-full h-full" style={{ background: '#040A08' }} />
      </motion.div>

      {/* Selected cluster drawer */}
      <AnimatePresence>
        {activeCluster && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            className="rounded-3xl border border-white/10 bg-card/80 backdrop-blur-xl p-4"
            data-testid="cluster-panel"
          >
            <ClusterDetail cluster={activeCluster} onClose={() => setActiveCluster(null)} />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function StatChip({ icon: Icon, label, value }) {
  return (
    <div className="rounded-2xl bg-card/60 border border-white/5 px-3 py-2 flex items-center gap-2">
      <Icon className="w-3.5 h-3.5 text-accent" strokeWidth={2} />
      <div className="min-w-0">
        <p className="font-mono text-base tabular-nums leading-none">{value}</p>
        <p className="text-[9px] uppercase tracking-widest text-muted-foreground mt-0.5">{label}</p>
      </div>
    </div>
  );
}

function ClusterDetail({ cluster, onClose }) {
  return (
    <>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-[10px] uppercase tracking-[0.25em] text-accent">{cluster.species_category}</p>
          <h3 className="font-serif text-xl leading-tight">{cluster.species_common_name}</h3>
          <p className="text-xs italic text-muted-foreground">{cluster.species_scientific_name}</p>
        </div>
        <button
          onClick={onClose}
          data-testid="close-cluster-panel"
          className="text-[10px] uppercase tracking-widest text-muted-foreground hover:text-foreground px-2 py-1 rounded-full border border-white/10"
        >Close</button>
      </div>
      {cluster.area && (
        <p className="mt-2 text-xs flex items-center gap-1.5 text-muted-foreground">
          <MapPin className="w-3 h-3" /> {cluster.area}
        </p>
      )}
      <div className="mt-3 grid grid-cols-3 gap-2">
        <MiniStat label="Sightings" value={cluster.count} />
        <MiniStat label="Finders" value={cluster.unique_finders} />
        <MiniStat label="Top Score" value={Math.max(...cluster.finders.map(f => f.discovery_score || 0))} />
      </div>
      <div className="mt-4">
        <p className="text-[10px] uppercase tracking-widest text-muted-foreground mb-2">Finders</p>
        <div className="space-y-1.5 max-h-56 overflow-y-auto pr-1">
          {cluster.finders.map((f, i) => (
            <div key={i} className="flex items-center gap-2 py-1.5 border-b border-white/5 last:border-0" data-testid="finder-row">
              {f.picture ? (
                <img src={f.picture} alt="" className="w-7 h-7 rounded-full object-cover" />
              ) : (
                <div className="w-7 h-7 rounded-full bg-primary/20 flex items-center justify-center text-[10px] text-accent">
                  {(f.name || 'A')[0]}
                </div>
              )}
              <div className="flex-1 min-w-0">
                <p className="text-sm truncate">{f.name}</p>
                <p className="text-[10px] text-muted-foreground">
                  {new Date(f.created_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
                </p>
              </div>
              <span className="font-mono text-xs text-accent">{f.discovery_score}</span>
            </div>
          ))}
        </div>
      </div>
    </>
  );
}

function MiniStat({ label, value }) {
  return (
    <div className="rounded-xl bg-black/30 border border-white/5 px-2.5 py-2 text-center">
      <p className="font-mono text-base tabular-nums leading-none">{value}</p>
      <p className="text-[9px] uppercase tracking-widest text-muted-foreground mt-1">{label}</p>
    </div>
  );
}
